import React, { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { MAX_INTERACTION_PLANE_SIZE } from '../../constants/canvas';

type GridProps = {
  positionZ?: number;
  opacity?: number;
  depthWrite?: boolean;
  depthTest?: boolean;
  renderOrder?: number;
};

const Grid: React.FC<GridProps> = ({
  positionZ = -0.05,
  opacity = 1,
  depthWrite = false,
  depthTest = false,
  renderOrder = -10000,
}) => {
  const { scene } = useThree();

  useEffect(() => {
    const size = MAX_INTERACTION_PLANE_SIZE * 1.5;

    const majorUnitSize = 1;   // Major grid lines every 1 unit
    const fineUnitSize = 0.1;  // Fine grid lines every 0.1 units

    // Calculate the number of major divisions (cells)
    const divisionsMajor = Math.ceil(size / majorUnitSize);

    // Calculate the number of fine divisions (cells)
    const divisionsFine = divisionsMajor * (majorUnitSize / fineUnitSize);

    // Adjust the grid size to align with the major units
    const adjustedSize = divisionsMajor * majorUnitSize;

    // Create the fine grid (lines every 0.1 units)
    const gridHelperFine = new THREE.GridHelper(
      adjustedSize,
      divisionsFine,
      0xdddddd, // Color of the center line (if any)
      0xdddddd  // Color of the grid lines
    );
    gridHelperFine.rotation.x = Math.PI / 2; // Align with XY plane
    gridHelperFine.position.z = positionZ;
    gridHelperFine.renderOrder = renderOrder;
    configureGridMaterial(gridHelperFine, opacity, depthWrite, depthTest);
    scene.add(gridHelperFine);

    // Create the major grid (lines every 1 unit)
    const gridHelperMajor = new THREE.GridHelper(
      adjustedSize,
      divisionsMajor,
      0xbbbbbb, // Color of the center line (if any)
      0xbbbbbb  // Color of the grid lines
    );
    gridHelperMajor.rotation.x = Math.PI / 2; // Align with XY plane
    gridHelperMajor.position.z = positionZ + 0.001; // Slight Z offset to prevent z-fighting
    gridHelperMajor.renderOrder = renderOrder + 1;
    configureGridMaterial(gridHelperMajor, opacity, depthWrite, depthTest);
    scene.add(gridHelperMajor);

    return () => {
      scene.remove(gridHelperFine);
      scene.remove(gridHelperMajor);
      disposeGrid(gridHelperFine);
      disposeGrid(gridHelperMajor);
    };
  }, [depthTest, depthWrite, opacity, positionZ, renderOrder, scene]);

  return null;
};

function configureGridMaterial(
  gridHelper: THREE.GridHelper,
  opacity: number,
  depthWrite: boolean,
  depthTest: boolean,
) {
  const materials = Array.isArray(gridHelper.material)
    ? gridHelper.material
    : [gridHelper.material];

  materials.forEach((material) => {
    material.transparent = opacity < 1;
    material.opacity = opacity;
    material.depthWrite = depthWrite;
    material.depthTest = depthTest;
  });
}

function disposeGrid(gridHelper: THREE.GridHelper) {
  gridHelper.geometry.dispose();
  const materials = Array.isArray(gridHelper.material)
    ? gridHelper.material
    : [gridHelper.material];
  materials.forEach((material) => material.dispose());
}

export default Grid;
